/**
 * Taalkeuze van de SmartSuite, gedeeld door alle panes (fase 1 van de pane-schil,
 * 21 september 2026; eerder alleen in DocSense).
 *
 * De taal volgt standaard de taal van Excel (`Office.context.displayLanguage`) en wordt
 * daarna bewaard in de werkmap-eigenschap `AGTT_Language`, dezelfde sleutel die de
 * Documentation-pane al gebruikte. Zo spreken de panes elkaar niet tegen: één keer
 * wisselen in de titelbalk geldt voor de hele werkmap.
 */

/* global Excel, Office */

export type Lang = "NL" | "EN";

/** Sleutel in de werkmap-eigenschappen. */
export const LANGUAGE_PROPERTY = "AGTT_Language";

/** Taal van Excel zelf: alles wat met `nl` begint wordt Nederlands, de rest Engels. */
export function detectExcelLanguage(): Lang {
  try {
    const tag = (
      Office?.context?.displayLanguage ||
      Office?.context?.contentLanguage ||
      ""
    ).toLowerCase();
    return tag.startsWith("nl") ? "NL" : "EN";
  } catch {
    return "NL";
  }
}

/**
 * Bewaarde taal uit de werkmap, of anders de taal van Excel. Alleen lezen: het openen van een
 * pane schrijft niets in de werkmap (Store-beleid 1100.4, STORE-16; besluit 5 oktober 2026).
 * De keuze wordt pas vastgelegd als de gebruiker wisselt (`saveLanguage`).
 */
export async function loadLanguage(): Promise<Lang> {
  try {
    let stored: string | undefined;
    await Excel.run(async (context) => {
      const properties = context.workbook.properties.custom;
      properties.load("items");
      await context.sync();
      stored = properties.items.find((p) => p.key === LANGUAGE_PROPERTY)?.value as
        | string
        | undefined;
    });
    if (stored === "NL" || stored === "EN") return stored;
    return detectExcelLanguage();
  } catch {
    return detectExcelLanguage();
  }
}

/** Taalkeuze vastleggen in de werkmap. */
export async function saveLanguage(lang: Lang): Promise<void> {
  try {
    await Excel.run(async (context) => {
      context.workbook.properties.custom.add(LANGUAGE_PROPERTY, lang);
      await context.sync();
    });
  } catch {
    /* niet kritiek: de taal blijft dan alleen voor deze sessie gelden */
  }
}

/** De andere taal, voor de wisselknop. */
export function otherLanguage(lang: Lang): Lang {
  return lang === "NL" ? "EN" : "NL";
}
